import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Avatar,
    Skeleton,
    Stack,
    IconButton,
    Modal,
} from "@mui/material";
import { ArrowForward, Mic, Search, Close as CloseIcon } from '@mui/icons-material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { RiHospitalLine } from 'react-icons/ri';
import { useNavigate } from "react-router-dom";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import MapSelector from "../Layout/MapSelector";
import VoiceRecorder from '../inputs/VoiceRecorder';
import Tooltip from '@mui/material/Tooltip';
import LaboratoryChatModal from "../modals/LaboratoryChatModal";
import PromoBanner from '../Layout/PromoBanner';
import { get, post } from '../../utility/fetchWeb';
import { get as gets, post as posts } from '../../utility/fetch';

import { useDrawer } from "../../contexts/DrawerContext";
import PaymentModal from "../Layout/PaymentModal";
import BankPaymentModal from "../Layout/BankPaymentModal";
import PaymentConfirmationModal from "../Layout/PaymentConfirmationModal";
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import VideoCalling from '../Layout/VideoCalling';
import Pagination from "../Layout/Pagination";

export default function LaboratoryPage() {
    const [selectedCategory, setSelectedCategory] = useState("General");
    const navigate = useNavigate();
    const [data, setData] = React.useState([]);
    const [categories, setCategories] = React.useState([]);
    const [engagedDoctors, setEngagedDoctors] = React.useState([]);
    const [searchText, setSearchText] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [filteredData, setFilteredData] = useState([]);
    const [currentLocation, setCurrentLocation] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [isVoiceModalOpen, setIsVoiceModalOpen] = useState(false);
    const [isListening, setIsListening] = useState(false);
    const [labServices, setLabServices] = useState([]);
    const [showVoiceResults, setShowVoiceResults] = useState(false);
    const [targetedAds, setTargetedAds] = useState([]);
    const [isChatModalOpen, setIsChatModalOpen] = useState(false);
    const [selectedLabForChat, setSelectedLabForChat] = useState(null);

    // New states for lab services modal
    const [labServicesModalOpen, setLabServicesModalOpen] = useState(false);
    const [selectedLab, setSelectedLab] = useState(null);
    const [labServicesLoading, setLabServicesLoading] = useState(false);
    const [labServiceSearch, setLabServiceSearch] = useState('');
    const [isVoiceLabServiceModalOpen, setIsVoiceLabServiceModalOpen] = useState(false);
    const [filteredLabServices, setFilteredLabServices] = useState([]);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [selectedService, setSelectedService] = useState(null);
    const [openBankModal, setOpenBankModal] = useState(false);
    const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);

    // Agora states
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [showVideoChatModal, setShowVideoChatModal] = useState(false);
    const [startAudioOnly, setStartAudioOnly] = useState(false);
    const [appId, setAppId] = useState(process.env.REACT_APP_AGORA_KEY);
    const [channel, setChannel] = useState("call");
    const [token, setToken] = useState(null);
    const [uid, setUid] = useState(sessionStorage.getItem("userId") || "defaultUid");
    const [selectedLabForCall, setSelectedLabForCall] = useState(null);
    const [callId, setCallId] = useState(null); // Add callId state

    const name = sessionStorage.getItem("name").toString();
    const query = new URLSearchParams(window.location.search);
    const chatId = query.get("rrt");

    const handleLocationSelect = (location, address) => {
        console.log("Selected Location:", location);
        console.log("Selected Address:", address);
    };

    const handleMessageClick = (e, lab) => {
        e.stopPropagation();
        setSelectedLabForChat(lab);
        setIsChatModalOpen(true);
    };

    // Handle cart click to open lab services modal
    const handleCartClick = (e, lab) => {
        e.stopPropagation();
        setSelectedLab(lab);
        setLabServicesModalOpen(true);
        fetchLabServices(lab.id);
    };

    // Close lab services modal
    const handleCloseLabServicesModal = () => {
        setLabServicesModalOpen(false);
        setSelectedLab(null);
        setLabServices([]);
        setFilteredLabServices([]);
        setLabServiceSearch('');
    };

    // Fetch lab services for selected lab
    const fetchLabServices = async (labId) => {
        setLabServicesLoading(true);
        try {
            // Update this endpoint according to your API
            const response = await gets(`/internallabservice/list/${labId}/1/50/by-health-care-provider`);
            console.log('Lab Services Response:', response);

            const services = response?.data.recordList || response?.data || [];
            setLabServices(services);
            setFilteredLabServices(services);
        } catch (error) {
            console.error('Error fetching lab services:', error);
            setLabServices([]);
            setFilteredLabServices([]);
        } finally {
            setLabServicesLoading(false);
        }
    };

    // Handle lab service search with API call
    const handleLabServiceSearchChange = (e) => {
        const searchValue = e.target.value;
        setLabServiceSearch(searchValue);

        if (searchValue.trim() === '') {
            // If search is empty, fetch all services
            fetchLabServices(selectedLab.id);
        } else if (searchValue.length >= 2) {
            // If search has at least 2 characters, use the filter API
            fetchFilteredLabServices(selectedLab.id, searchValue);
        }
    };

    // Fetch filtered lab services using the search API
    const fetchFilteredLabServices = async (labId, searchQuery) => {
        setLabServicesLoading(true);
        try {
            // Using the search endpoint you provided
            const response = await gets(`/internallabservice/filter-list/${labId}/testName/${searchQuery}/1/50/by-health-care-provider`);
            console.log('Filtered Lab Services Response:', response);

            const services = response?.data.recordList || response?.data || [];
            setLabServices(services);
            setFilteredLabServices(services);
        } catch (error) {
            console.error('Error fetching filtered lab services:', error);
            // Fallback to local filtering if API fails
            const filtered = labServices.filter(service =>
                service.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                service.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                service.testName?.toLowerCase().includes(searchQuery.toLowerCase())
            );
            setFilteredLabServices(filtered);
        } finally {
            setLabServicesLoading(false);
        }
    };

    // Handle voice search for lab services
    const handleLabServiceVoiceSearch = () => {
        setIsVoiceLabServiceModalOpen(true);
    };

    // Handle voice transcript for lab services
    const handleLabServiceVoiceTranscript = async (transcript) => {
        setLabServiceSearch(transcript);

        if (transcript.trim().length >= 2) {
            // Use the API search for voice input
            await fetchFilteredLabServices(selectedLab.id, transcript);
        }

        setIsVoiceLabServiceModalOpen(false);
    };

    const getEngagedDoctors = async () => {
        try {
            const response = await get(`/Doctors/EngagedDoctors/${sessionStorage.getItem("userId")}`);
            console.log('Response:', response);
            setEngagedDoctors(response);
        } catch (error) {
            console.error("Error fetching doctors:", error);
        }
    }

    const getHealthCareProvider = async (page = 1) => {
        setIsLoading(true);
        try {
            let response;
            response = await get(`/HealthCareProvider/list/HealthCareProviderCategory/${4}/true?pageNumber=${page}&pageSize=10`);
            console.log('Response:', response);
            setData(response?.data?.recordList);
            setFilteredData(response?.data?.recordList);
            setTotalPages(response?.data?.metadata.totalPages || 1);
            setCurrentPage(response?.data?.metadata.page || 1);
            setIsLoading(false);
        } catch (error) {
            console.error('Error fetching doctors:', error);
            setIsLoading(false);
        }
    }

    const getSearchedHealthCareProvider = async (searchText, page = 1) => {
        setIsLoading(true);
        try {
            let response;
            response = await get(`/HealthCareProvider/filter-list/true/categoryType/Laboratory/Name/${searchText}?pageNumber=${page}&pageSize=10`);
            console.log('Response:', response);
            setData(response?.data?.recordList);
            setFilteredData(response?.data?.recordList);
            setTotalPages(response?.data?.metadata.totalPages || 1);
            setCurrentPage(response?.data?.metadata.page || 1);
            setIsLoading(false);
        } catch (error) {
            console.error('Error fetching doctors:', error);
            setData([]);
            setFilteredData([]);
            setIsLoading(false);

        }
    }

    const handleChange = (event) => {
        const { value } = event.target;
        console.log("Search Text:", value);
        setSearchText(value);
        if (value.length >= 2) {
            getSearchedHealthCareProvider(value, currentPage);
        } else if (value.length === 0) {
            getHealthCareProvider();
        }
    }

    const handleSearchChange = (e) => {
        const { value } = e.target;
        setSearchQuery(value);
        if (value.length >= 2) {
            getSearchedHealthCareProvider(value, currentPage);
        } else if (value.length === 0) {
            getHealthCareProvider(currentPage);
        }
    };

    const handleMicClick = () => {
        setIsVoiceModalOpen(true);
    };

    const handleTranscript = async (transcript) => {
        setIsLoading(true);
        setSearchQuery(transcript);
        setIsListening(true);
        try {
            setShowVoiceResults(true);
            setIsVoiceModalOpen(false);
        } catch (error) {
            console.error('Error fetching laboratories:', error);
        } finally {
            setIsListening(false);
            setIsLoading(false);
        }
    };

    const getcategories = async () => {
        try {
            const response = await get('/categories/specialistCategories');
            console.log(response);
            setCategories(response.data);
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    }

    const getTargetedAds = async () => {
        try {
            const userId = sessionStorage.getItem('userId');
            const pageName = 'Lab Search';
            const response = await fetch(`${process.env.REACT_APP_BASE_URL}/connecthealthadmin/api/ad/find-ad?Id=${userId}&page=${pageName}`);

            if (response.ok) {
                const data = await response.json();
                const transformedAds = data.map(ad => ({
                    id: ad.id,
                    imageUrl: ad.bannerImageUrl,
                    title: ad.title,
                    subtitle: ad.description,
                    redirectUrl: ad.redirectUrl,
                    status: 1,
                    isTargeted: true
                }));
                setTargetedAds(transformedAds);
            }
        } catch (error) {
            console.error('Error fetching targeted ads:', error);
            setTargetedAds([]);
        }
    };

    const getAgoraDetails = async (receiverId) => {
        try {
            const res = await get(`/posts/generate-new-token?channelName=${`connecthealthpro_videocall-${uid}&${receiverId}`}&uid=${uid}`);
            if (res && res.token) {
                setToken(res.token);
                setUid(Number(res.uid) || uid);
                setAppId(res.appId || process.env.REACT_APP_AGORA_KEY);
                setChannel(res.channelName || "call");
                return true;
            }
            return false;
        } catch (error) {
            console.error("Error fetching Agora token:", error);
            return false;
        }
    };

    const notifyCaller = async (receiverId) => {
        try {
            const res = await post(`/Posts/notify-call`, {
                receiverId: receiverId,
                channelName: channel,
                callerId: Number(sessionStorage.getItem("userId")),
                callerName: sessionStorage.getItem("name"),
                userType: 2 // for practitioner 1 for patient
            });
            console.log(res)
            if (res?.status === 'success') {
                console.log("Caller notified successfully");
            } else {
                console.error("Failed to notify caller:", res);
            }
        } catch (error) {
            console.error("Error notifying caller:", error);
        }
    }

    const handleVideoCall = async (e, lab) => {
        e.stopPropagation();
        setSelectedLabForCall(lab);
        const success = await getAgoraDetails(lab.contactPersonId);
        if (success) {
            setStartAudioOnly(false);
            setCallId(Date.now()); // Generate unique callId
            setShowVideoChatModal(true);
            notifyCaller(lab.contactPersonId);
        }
    };

    const handleAudioCall = async (e, lab) => {
        e.stopPropagation();
        setSelectedLabForCall(lab);
        const success = await getAgoraDetails(lab.contactPersonId);
        if (success) {
            setStartAudioOnly(true);
            setCallId(Date.now()); // Generate unique callId
            setShowVideoChatModal(true);
            notifyCaller(lab.contactPersonId);
        }
    };

    const handleCloseVideoChat = () => {
        setShowVideoChatModal(false);
        setToken(null);
        setSelectedLabForCall(null);
        setCallId(null); // Reset callId
    };

    useEffect(() => {
        getHealthCareProvider();
        getEngagedDoctors();
        getTargetedAds();

        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { latitude, longitude } = position.coords;
                    setCurrentLocation({ lat: latitude, lng: longitude });
                },
                (error) => {
                    console.error("Error getting current location:", error);
                }
            );
        }
    }, [])

    useEffect(() => {
        if (searchQuery.length >= 3) {
            getSearchedHealthCareProvider(searchQuery, currentPage);
        } else if (searchQuery.length === 0) {
            getHealthCareProvider(currentPage);
        }
    }, [searchQuery]);

    return (
        <div className="pharmacy-page w-100">
            <Box display="flex" alignItems="center">
                <Avatar sx={{ width: 64, height: 64, mr: 3, backgroundColor: 'primary.main' }}>
                    <AccountCircleIcon fontSize="large" />
                </Avatar>
                <Box>
                    <Typography variant="subtitle2" color="text.secondary">Good Day</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 0.5 }}>{name}</Typography>
                </Box>
            </Box>

            {/* Search Bar Section */}
            <div className="search-section">
                <div className="search-bar">
                    <input
                        type="text"
                        placeholder="Search for laboratories..."
                        value={searchQuery}
                        onChange={handleSearchChange}
                    />
                    {(searchQuery) && (
                        <button
                            className="clear-button"
                            onClick={() => {
                                setSearchQuery('');
                            }}
                        >
                            ×
                        </button>
                    )}
                </div>
                <Tooltip title="Voice search for laboratory" arrow placement="top">
                    <div className="filter-btn" onClick={handleMicClick}>
                        <Mic color='white' />
                    </div>
                </Tooltip>
            </div>

            {/* Filter Bar */}
            <Box display="flex" flexDirection={"column"} justifyContent="flex-end" gap={2} mt={2} mb={3}>
                <Box sx={{ flex: 1 }}>
                    <MapSelector
                        onLocationSelect={handleLocationSelect}
                        currentLocation={currentLocation}
                    />
                </Box>
            </Box>

            {/* Add PromoBanner after search section */}
            {targetedAds.length > 0 && (
                <Box sx={{ mb: 3 }}>
                    <PromoBanner ads={targetedAds} />
                </Box>
            )}

            <>
                {/* Voice Search Modal */}
                {isVoiceModalOpen && (
                    <VoiceRecorder
                        onTranscript={async (transcript) => {
                            if (transcript) {
                                await handleTranscript(transcript);
                                setShowVoiceResults(true);
                            }
                            setIsVoiceModalOpen(false);
                            setIsListening(false);
                        }}
                        provider="Laboratory"
                    />
                )}

                {isLoading ? (
                    <div className="pharmacy-list">
                        {[...Array(6)].map((_, index) => (
                            <div key={index} className="pharmacy-card">
                                <Skeleton variant="circular" width={56} height={56} />
                                <div className="pharmacy-info">
                                    <Skeleton variant="text" width="60%" height={20} />
                                    <Skeleton variant="text" width="80%" height={24} />
                                    <Skeleton variant="text" width="90%" height={16} />
                                </div>
                                <div className="doctor-info">
                                    <Skeleton variant="text" width="70%" height={20} />
                                    <Skeleton variant="text" width="50%" height={16} />
                                </div>
                                <Stack direction="row" spacing={1}>
                                    <Skeleton variant="circular" width={40} height={40} />
                                    <Skeleton variant="circular" width={40} height={40} />
                                    <Skeleton variant="circular" width={40} height={40} />
                                </Stack>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="pharmacy-list">
                        <>
                            {data?.length > 0 ? (
                                <>
                                    {data?.map((lab) => (
                                        <div key={lab?.id} className="pharmacy-card" onClick={() => navigate(`/profile?cat=caregiverLab&id=${lab?.id}`, { state: { providerData: lab } })}>
                                            <div className="pharmacy-icon">
                                                <Avatar src={lab?.logoPath} sx={{ width: 56, height: 56, backgroundColor: 'white' }} variant="rounded">
                                                    {!lab?.logoPath && <RiHospitalLine size={32} color="white" />}
                                                </Avatar>
                                            </div>

                                            <div className="pharmacy-info">
                                                <div className="pharmacy-type">{lab?.healthCareProviderCategory?.name || 'Laboratory'}</div>
                                                <h4 className="pharmacy-name">
                                                    {lab?.name}
                                                </h4>
                                                <p className="pharmacy-address">
                                                    {lab?.location || 'No location provided'}
                                                </p>
                                            </div>

                                            <div className="doctor-info">
                                                <h5 className="doctor-name">
                                                    Lab Services
                                                </h5>
                                                <p className="doctor-role">
                                                    Diagnostic Tests
                                                </p>
                                            </div>

                                            <Stack direction="row" spacing={1}>
                                                <Tooltip title="Order Test" arrow>
                                                    <IconButton size="medium" color="primary" onClick={(e) => handleCartClick(e, lab)}>
                                                        <ShoppingCartOutlinedIcon fontSize="medium" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Message Laboratory" arrow>
                                                    <IconButton size="medium" color="primary" onClick={(e) => handleMessageClick(e, lab)}>
                                                        <ChatBubbleOutlineIcon fontSize="medium" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Video Call" arrow>
                                                    <IconButton size="medium" color="primary" onClick={(e) => handleVideoCall(e, lab)}>
                                                        <VideocamOutlinedIcon fontSize="medium" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Audio Call" arrow>
                                                    <IconButton size="medium" color="primary" onClick={(e) => handleAudioCall(e, lab)}>
                                                        <PhoneOutlinedIcon fontSize="medium" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </div>
                                    ))}
                                </>
                            ) : (
                                <Box sx={{ textAlign: "center", mt: 4 }}>
                                    <Typography variant="h6" color="text.secondary">
                                        We couldn't find any laboratory matching your search.
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        You can browse through the available laboratories, search by name, or try a different location.
                                    </Typography>
                                </Box>
                            )}
                        </>


                        {/* Lab Services Modal */}
                        <Modal
                            open={labServicesModalOpen}
                            onClose={handleCloseLabServicesModal}
                            aria-labelledby="lab-services-modal-title"
                            aria-describedby="lab-services-modal-description"
                            sx={{
                                '& .MuiBackdrop-root': { backgroundColor: 'rgba(0,0,0,0.5)' }
                            }}
                        >
                            <Box sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: '50vw',
                                maxWidth: '1200px',
                                bgcolor: 'background.paper',
                                borderRadius: 3,
                                boxShadow: 24,
                                p: 4,
                                height: '80vh',
                                overflowY: 'auto',
                                '::-webkit-scrollbar': { display: 'none' },
                                scrollbarWidth: 'none'
                            }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                                    <Typography id="lab-services-modal-title" variant="h6" component="h2">
                                        {selectedLab?.name} - Available Lab Services
                                    </Typography>
                                    <IconButton onClick={handleCloseLabServicesModal}>
                                        <CloseIcon />
                                    </IconButton>
                                </Stack>

                                <Box display="flex" gap={1} mb={2}>
                                    <input
                                        type="text"
                                        placeholder="Search for lab services..."
                                        value={labServiceSearch}
                                        onChange={handleLabServiceSearchChange}
                                        style={{
                                            flex: 1,
                                            padding: '8px 12px',
                                            borderRadius: '8px',
                                            border: '1px solid #e0e0e0',
                                            fontSize: '15px'
                                        }}
                                    />
                                    <IconButton onClick={handleLabServiceVoiceSearch} color="primary">
                                        <Mic />
                                    </IconButton>
                                </Box>

                                {/* Voice Search Modal for Lab Services */}
                                {isVoiceLabServiceModalOpen && (
                                    <Box sx={{
                                        position: 'fixed',
                                        top: 0,
                                        left: 0,
                                        width: '100vw',
                                        height: '100vh',
                                        bgcolor: 'rgba(0,0,0,0.3)',
                                        zIndex: 1300,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        <Box sx={{
                                            position: 'relative',
                                            bgcolor: 'background.paper',
                                            borderRadius: 2,
                                            boxShadow: 8,
                                            p: 3,
                                            minWidth: 320,
                                            maxWidth: 400,
                                            width: '90vw'
                                        }}>
                                            <IconButton
                                                sx={{ position: 'absolute', top: 8, right: 8, zIndex: 10 }}
                                                onClick={() => setIsVoiceLabServiceModalOpen(false)}
                                            >
                                                <CloseIcon />
                                            </IconButton>
                                            <VoiceRecorder
                                                onTranscript={async (transcript) => {
                                                    if (transcript) {
                                                        await handleLabServiceVoiceTranscript(transcript);
                                                    }
                                                    setIsVoiceLabServiceModalOpen(false);
                                                    setIsListening(false);
                                                }}
                                                provider={"Lab Service"}
                                            />
                                        </Box>
                                    </Box>
                                )}

                                {labServicesLoading ? (
                                    <Stack gap={2}>
                                        {[...Array(5)].map((_, idx) => (
                                            <Skeleton key={idx} variant="rectangular" height={48} />
                                        ))}
                                    </Stack>
                                ) : (
                                    <Stack gap={2}>
                                        {filteredLabServices.length === 0 ? (
                                            <Typography color="text.secondary">
                                                No lab services found.
                                            </Typography>
                                        ) : (
                                            filteredLabServices.map((service) => (
                                                <Box key={service.id} sx={{
                                                    p: 2,
                                                    borderRadius: 1,
                                                    bgcolor: '#f8f9fa',
                                                    boxShadow: 1,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: 0.5,
                                                    cursor: 'pointer',
                                                    '&:hover': { backgroundColor: '#e9ecef' }
                                                }}
                                                    onClick={() => {
                                                        // Handle service selection - you can add payment modal here
                                                        console.log('Selected service:', service);
                                                        setShowPaymentModal(true);
                                                        setLabServicesModalOpen(false);
                                                        setSelectedService(service);
                                                    }}>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                                                        {service.testName || service.name || service.serviceName}
                                                    </Typography>
                                                    {service.description && (
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            {service.description}
                                                        </Typography>
                                                    )}
                                                    <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
                                                        ₦{service.price || service.cost || service.amount || 'Price not available'}
                                                    </Typography>
                                                </Box>
                                            ))
                                        )}
                                    </Stack>
                                )}
                            </Box>
                        </Modal>

                        <LaboratoryChatModal
                            open={isChatModalOpen}
                            handleClose={() => {
                                setIsChatModalOpen(false);
                                setSelectedLabForChat(null);
                            }}
                            selectedLab={selectedLabForChat}
                        />

                        {showVideoChatModal && selectedLabForCall && (
                            <VideoCalling
                                appId={appId}
                                channel={channel}
                                token={token}
                                uid={uid}
                                callId={callId} // Pass callId
                                open={showVideoChatModal}
                                onClose={handleCloseVideoChat}
                                startWithVideoOff={startAudioOnly}
                                isAudio={startAudioOnly}
                            />
                        )}
                    </div>
                )}
            </>

            {/* Payment Modals */}
            {showPaymentModal && (
                <PaymentModal
                    open={showPaymentModal}
                    onClose={() => setShowPaymentModal(false)}
                    service={selectedService}
                    hcProviderType="laboratory"
                    hcProvider={selectedLab}
                    openBankModal={() => setOpenBankModal(true)}
                />
            )}

            {openBankModal && (
                <BankPaymentModal
                    open={openBankModal}
                    onClose={() => setOpenBankModal(false)}
                    hcProviderType="laboratory"
                    hcProvider={selectedLab}
                    service={selectedService}
                    onPay={() => {
                        setOpenBankModal(false);
                        setOpenConfirmationModal(true);
                    }}
                />
            )}

            <PaymentConfirmationModal
                open={openConfirmationModal}
                onClose={() => setOpenConfirmationModal(false)}
                userName={sessionStorage.getItem("name") || "User"}
                doctorName={selectedLab?.name || "Laboratory"}
                service={selectedService}
                hcProviderType="laboratory"
                hcProvider={selectedLab}
                onAddToCalendar={() => {
                    setOpenConfirmationModal(false);
                    navigate(`/profile?cat=caregiver&id=${selectedLab?.id}`, { state: { providerData: selectedLab } })
                }}
            />

            <style jsx>{`
                .pharmacy-page {
                    padding: 24px;
                    margin: 0 auto;
                }

                .search-section {
                    display: flex;
                    gap: 12px;
                    margin: 24px 0;
                    align-items: center;
                }

                .search-bar {
                    flex: 1;
                    position: relative;
                    display: flex;
                    align-items: center;
                    background: #f8f9fa;
                    border-radius: 12px;
                    padding: 0 16px;
                    border: 1px solid #e9ecef;
                }

                .search-icon {
                    color: #6c757d;
                    margin-right: 12px;
                }

                .search-bar input {
                    flex: 1;
                    border: none;
                    background: transparent;
                    padding: 16px 0;
                    font-size: 16px;
                    outline: none;
                }

                .search-bar input::placeholder {
                    color: #6c757d;
                }

                .filter-btn {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 48px;
                    height: 48px;
                    border-radius: 50%;
                    background: #007bff;
                    cursor: pointer;
                    transition: transform 0.2s;
                }

                .filter-btn:hover {
                    transform: scale(1.05);
                }

                .pharmacy-list {
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                }

                .pharmacy-card {
                    display: flex;
                    align-items: center;
                    padding: 20px;
                    background: white;
                    border-radius: 12px;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
                    cursor: pointer;
                    transition: transform 0.2s, box-shadow 0.2s;
                    gap: 16px;
                }

                .pharmacy-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
                }

                .pharmacy-info {
                    flex: 1;
                    min-width: 0;
                }

                .pharmacy-type {
                    color: #6c757d;
                    font-size: 12px;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    margin-bottom: 4px;
                }

                .pharmacy-name {
                    font-size: 18px;
                    font-weight: 600;
                    color: #212529;
                    margin: 0 0 8px 0;
                }

                .pharmacy-address {
                    color: #6c757d;
                    font-size: 14px;
                    margin: 0;
                }

                .doctor-info {
                    flex: 1;
                    min-width: 0;
                    text-align: right;
                }

                .doctor-name {
                    font-size: 14px;
                    font-weight: 600;
                    color: #212529;
                    margin: 0 0 4px 0;
                }

                .doctor-role {
                    color: #6c757d;
                    font-size: 12px;
                    margin: 0;
                }

                .pharmacy-icon {
                    width: 56px;
                    height: 56px;
                    background: linear-gradient(135deg, #6f42c1, #e83e8c);
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    font-size: 24px;
                    flex-shrink: 0;
                }
            `}</style>
            {!isLoading && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={getHealthCareProvider}
                />
            )}
        </div>
    );
}